import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { getTranslation } from '../../utils/translations';
import { getApiUrl } from '../../config/constants';
import PhoneInput from './PhoneInput';
import OtpInput from './OtpInput';
import ProfileCompletion from './ProfileCompletion';
import './LoginModal.css';

const LoginModal = ({ isOpen, onClose, onSuccess, language }) => {
  // Step management: 'phone' -> 'otp' -> 'profile' (for new users)
  const [currentStep, setCurrentStep] = useState('phone');
  const [phoneData, setPhoneData] = useState({
    phone: '',
    countryCode: '+966',
    fullNumber: ''
  });
  const [tempToken, setTempToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentStep('phone');
      setPhoneData({ phone: '', countryCode: '+966', fullNumber: '' });
      setTempToken('');
      setError('');
      setIsSuccess(false);
      setIsLoading(false);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handlePhoneChange = (data) => {
    setPhoneData(data);
    // Clear error when user changes phone
    if (error) {
      setError('');
    }
  };

  const handleSendOtp = async () => {
    if (!phoneData.phone || !phoneData.countryCode) {
      setError(getTranslation('enterPhoneNumber', language));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/auth/otp/send'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneData.phone,
          country_code: phoneData.countryCode
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setCurrentStep('otp');
        setError('');
      } else {
        setError(data.message || getTranslation('loginError', language));
      }
    } catch (err) {
      setError(getTranslation('connectionError', language));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otpCode) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/auth/otp/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          phone: phoneData.phone,
          country_code: phoneData.countryCode,
          otp_code: otpCode
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.is_new_user) {
          // New user - show profile completion
          setTempToken(data.temp_token);
          setCurrentStep('profile');
        } else {
          // Existing user - login successful
          setIsSuccess(true);
          localStorage.setItem('user', JSON.stringify(data.user));
          localStorage.setItem('token', data.token);

          setTimeout(() => {
            if (onSuccess) {
              onSuccess();
            } else {
              onClose();
              window.location.reload();
            }
          }, 1500);
        }
      } else {
        setError(data.message || getTranslation('invalidOtp', language));
      }
    } catch (err) {
      setError(getTranslation('connectionError', language));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteProfile = async (profileData) => {
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(getApiUrl('/auth/otp/complete-profile'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(profileData)
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSuccess(true);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);

        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          } else {
            onClose();
            window.location.reload();
          }
        }, 1500);
      } else {
        setError(data.message || getTranslation('loginError', language));
      }
    } catch (err) {
      setError(getTranslation('connectionError', language));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="login-modal-overlay"
      onClick={handleOverlayClick}
    >
      <div className={`login-modal ${language === 'ar' ? 'rtl' : 'ltr'}`}>
        {/* Header */}
        <div className="login-modal-header">
          <h2 className="login-modal-title">
            {getTranslation('login', language)}
          </h2>
          <button
            className="login-modal-close-btn"
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>

        {/* Content */}
        <div className="login-modal-content">
          {isSuccess ? (
            <div className="login-success-message">
              <div className="success-icon">✓</div>
              <p>{getTranslation('loginSuccess', language)}</p>
            </div>
          ) : (
            <>
              {currentStep === 'phone' && (
                <div className="phone-step">
                  <PhoneInput
                    value={phoneData.phone}
                    onChange={handlePhoneChange}
                    language={language}
                    error={error}
                    disabled={isLoading}
                    countryCode={phoneData.countryCode}
                  />

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    className="login-submit-btn"
                    disabled={isLoading || !phoneData.phone}
                  >
                    {isLoading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      getTranslation('sendOtp', language)
                    )}
                  </button>
                </div>
              )}

              {currentStep === 'otp' && (
                <div className="otp-step">
                  <OtpInput
                    onComplete={handleVerifyOtp}
                    onResend={handleSendOtp}
                    language={language}
                    error={error}
                    disabled={isLoading}
                    phoneNumber={phoneData.phone}
                    countryCode={phoneData.countryCode}
                  />
                </div>
              )}

              {currentStep === 'profile' && (
                <div className="profile-step">
                  <ProfileCompletion
                    onComplete={handleCompleteProfile}
                    language={language}
                    phoneNumber={phoneData.phone}
                    countryCode={phoneData.countryCode}
                    tempToken={tempToken}
                    isLoading={isLoading}
                  />
                </div>
              )}

              {/* Error Message */}
              {error && currentStep !== 'otp' && (
                <div className="error-message">
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LoginModal;
